import {
	ACTION_TYPES,
	ACTION_BUILDING_TYPES,
	RENOVATION_OR_EXTENSION_ACTION_TYPES,
	actionTypeCheckboxes,
} from "@dune/vars";
import {
	ConstructionSiteFullDetails,
	GFAFullDetails,
	ConstructionSiteDocument,
	ConstructionSiteDocumentRaw,
	ConstructionSiteList,
	ConstructionSiteStatus,
	ClientType,
	GFAStatus,
} from "@dune/types";
import { ConstructionSiteDetails, Prisma } from "@prisma/client";
import { ConstructionSiteWithClientDetails } from "@dune/types";

export const getActionTypeLabel = (type: string): string => {
	switch (type) {
		case "CONSTRUCTION":
			return ACTION_TYPES.CONSTRUCTION;
		case "RENOVATION":
			return ACTION_TYPES.RENOVATION;
		case "EXTENSION":
			return ACTION_TYPES.EXTENSION;
		default:
			return type;
	}
};

export const getActionTypesLabel = (types: string[]): string => {
	return types.map((type) => getActionTypeLabel(type)).join(", ");
};

export const getBuildingTypeLabel = (
	buildingType: string | undefined,
): string => {
	if (!buildingType) return "";
	switch (buildingType) {
		case "APPARTMENT":
			return ACTION_BUILDING_TYPES.APPARTMENT;
		case "INDIVIDUAL_HOUSE":
			return ACTION_BUILDING_TYPES.INDIVIDUAL_HOUSE;
		case "LIVING_BUILDING":
			return ACTION_BUILDING_TYPES.LIVING_BUILDING;
		case "COMMERCIAL_BUILDING":
			return ACTION_BUILDING_TYPES.COMMERCIAL_BUILDING;
		case "LOT_HOUSES":
			return ACTION_BUILDING_TYPES.LOT_HOUSES;
		case "OFFICE_BUILDING":
			return ACTION_BUILDING_TYPES.OFFICE_BUILDING;
		case "MIXT_BUILDING":
			return ACTION_BUILDING_TYPES.MIXT_BUILDING;
		default:
			return "";
	}
};

export const getRenovationExtensionActionLabel = (
	actionType: string,
): string => {
	switch (actionType) {
		case "interventionOnLoadBearingStructure":
			return RENOVATION_OR_EXTENSION_ACTION_TYPES.interventionOnLoadBearingStructure;
		case "underpinningExistingFoundations":
			return RENOVATION_OR_EXTENSION_ACTION_TYPES.underpinningExistingFoundations;
		default:
			return actionType;
	}
};

export const formatFloorLabel = (floor: number, isBasement = false): string => {
	const indication = isBasement ? "(R-)" : "(R+)";
	const pluralSuffix = Math.abs(floor) > 1 ? "s" : "";
	const floorType = isBasement ? "sous-sol" : "étage";

	return `${Math.abs(floor)} ${floorType}${pluralSuffix} ${indication}`;
};

export const formatSurfaceLabel = (
	surface: number,
	surfaceType?: string,
): string => {
	const baseLabel = `${surface} m²`;
	if (!surfaceType) return baseLabel;

	switch (surfaceType) {
		case "habitations":
			return `${baseLabel} de logements`;
		case "offices":
			return `${baseLabel} de bureaux`;
		case "commercial":
			return `${baseLabel} de commerces`;
		default:
			return baseLabel;
	}
};

export const formatDwellingLabel = (count: number): string => {
	const pluralSuffix = count > 1 ? "s" : "";
	return `${count} logement${pluralSuffix}`;
};

export const formatBuildingLabel = (count: number): string => {
	return `${count} bâtiment(s)`;
};

export const getCheckboxesToHide = (types: string[]) => {
	if (types.length === 0) {
		return {
			typeCheckboxesToHide: [],
			buildingTypeCheckboxesToHide: [
				"APPARTMENT",
				"INDIVIDUAL_HOUSE",
				"LIVING_BUILDING",
				"COMMERCIAL_BUILDING",
				"LOT_HOUSES",
				"OFFICE_BUILDING",
				"MIXT_BUILDING",
			],
		};
	}

	const typeCheckboxesToHide: string[] = [];
	const buildingTypeCheckboxesToHide: string[] = [];

	const selectedTypesCheckboxes = actionTypeCheckboxes.filter((typeCheckbox) =>
		types.includes(typeCheckbox.value),
	);

	selectedTypesCheckboxes.map((typeCheckbox) => {
		typeCheckbox?.typeCheckboxesToHide?.map((checkboxValue: string) =>
			typeCheckboxesToHide.push(checkboxValue),
		);
		typeCheckbox?.buildingTypeCheckboxesToHide?.map((checkboxId) =>
			buildingTypeCheckboxesToHide.push(checkboxId),
		);
	});

	return {
		typeCheckboxesToHide: [...new Set(typeCheckboxesToHide)],
		buildingTypeCheckboxesToHide: [...new Set(buildingTypeCheckboxesToHide)],
	};
};

/**
 * Helper function for deep equality comparison
 */
const deepEqual = (a: any, b: any): boolean => {
	if (a === b) return true;
	if (a == null || b == null) return a === b;
	if (Array.isArray(a) && Array.isArray(b)) {
		return (
			a.length === b.length &&
			a.every((item, index) => deepEqual(item, b[index]))
		);
	}
	if (typeof a === "object" && typeof b === "object") {
		const keysA = Object.keys(a);
		const keysB = Object.keys(b);
		return (
			keysA.length === keysB.length &&
			keysA.every((key) => deepEqual(a[key], b[key]))
		);
	}
	return false;
};

/**
 * Returns an object with the same structure but only containing changed fields
 * Preserves nested hierarchy and only includes fields that have actually changed
 */
export const getChangedFieldsObject = (
	originalData: any,
	newData: any,
): any => {
	if (originalData === newData) return {};
	if (originalData == null || newData == null) return newData;

	// Handle arrays - if they're different, return the entire new array
	if (Array.isArray(originalData) && Array.isArray(newData)) {
		if (!deepEqual(originalData, newData)) {
			return newData;
		}
		return {};
	}

	// Handle primitives
	if (typeof originalData !== "object" || typeof newData !== "object") {
		return originalData !== newData ? newData : {};
	}

	// Handle objects
	const result: any = {};
	const allKeys = new Set([
		...Object.keys(originalData || {}),
		...Object.keys(newData || {}),
	]);

	for (const key of allKeys) {
		const oldValue = originalData?.[key];
		const newValue = newData?.[key];

		// If key exists only in new data, include it
		if (!(key in (originalData || {})) && key in (newData || {})) {
			result[key] = newValue;
			continue;
		}

		// If key was removed, we might want to represent this differently
		// For now, we'll skip removed keys
		if (key in (originalData || {}) && !(key in (newData || {}))) {
			continue;
		}

		// Recursively check nested objects
		if (
			typeof oldValue === "object" &&
			typeof newValue === "object" &&
			oldValue !== null &&
			newValue !== null &&
			!Array.isArray(oldValue) &&
			!Array.isArray(newValue)
		) {
			const nestedChanges = getChangedFieldsObject(oldValue, newValue);
			if (Object.keys(nestedChanges).length > 0) {
				result[key] = nestedChanges;
			}
		} else {
			// For primitives, arrays, or null values
			if (!deepEqual(oldValue, newValue)) {
				result[key] = newValue;
			}
		}
	}

	return result;
};

export const convertUndefinedToNull = (obj: any): any => {
	if (obj === null || typeof obj !== "object") {
		return obj === undefined ? null : obj;
	}

	if (Array.isArray(obj)) {
		return obj.map(convertUndefinedToNull);
	}

	const converted: any = {};
	for (const [key, value] of Object.entries(obj)) {
		converted[key] = convertUndefinedToNull(value);
	}
	return converted;
};

export const mapConstructionSiteStatus = (
	status: ConstructionSiteStatus,
): string => {
	switch (status) {
		case "NEW":
			return "Nouveau";
		case "IN_PROGRESS":
			return "En cours";
		case "VALIDATED":
			return "Validé";
		case "REFUSED":
			return "Refusé";
		case "TO_RECEIVE":
			return "À réceptionner";
		case "RECEIVED":
			return "Réceptionné";
		default:
			return "Inconnu";
	}
};

export const getDetailsConstructionCost = (
	constructionSiteDetails: Partial<ConstructionSiteDetails> | null | undefined,
) => {
	if (!constructionSiteDetails) return 0;
	return (
		constructionSiteDetails.totalAmountHT ||
		constructionSiteDetails.totalAmountTTC ||
		0
	);
};

export const constructionSiteStatusStyles: Record<
	ConstructionSiteStatus | GFAStatus,
	string
> = {
	NEW: "border-blue-badge-border bg-blue-badge-background text-blue-badge-text",
	IN_PROGRESS:
		"border-orange-badge-border bg-orange-badge-background text-orange-badge-text",
	TO_ANALYZE:
		"border-orange-badge-border bg-orange-badge-background text-orange-badge-text",
	VALIDATED:
		"border-green-badge-border bg-green-badge-background text-green-badge-text",
	REFUSED:
		"border-red-badge-border bg-red-badge-background text-red-badge-text",
	TO_RECEIVE:
		"border-purple-badge-border bg-purple-badge-background text-purple-badge-text",
	RECEIVED:
		"border-purple-badge-border bg-purple-badge-background text-purple-badge-text",
	UNDER_WARRANTY:
		"border-purple-badge-border bg-purple-badge-background text-purple-badge-text",
} as const;

/**
 * Extrait le nom du client d'un chantier de construction
 * @param site - Le site de construction avec les détails client
 * @returns Le nom du client ou "Client inconnu" par défaut
 * Si pas de société, fusionne tous les noms des contacts séparés par des virgules
 */
export function getConstructionSiteClientName(
	site: ConstructionSiteWithClientDetails,
): string {
	if (site.constructionSiteClientDetails?.clientSociety?.denomination) {
		return site.constructionSiteClientDetails.clientSociety.denomination;
	}

	const contacts =
		site.constructionSiteClientDetails?.constructionSiteClientContacts;

	if (contacts && contacts.length > 0) {
		return contacts
			.map(
				(contact) =>
					`${contact.constructionSiteContact.firstname} ${contact.constructionSiteContact.lastname}`,
			)
			.join(", ");
	}

	return "Client inconnu";
}

export function getClientDenomination(
	constructionSite: ConstructionSiteFullDetails,
): string {
	if (
		constructionSite.constructionSiteClientDetails?.clientType === "SOCIETY"
	) {
		return (
			constructionSite.constructionSiteClientDetails?.clientSociety
				?.denomination || "Société non définie"
		);
	}

	// For USER type clients, we'll use MOA contact info if available
	if (constructionSite.constructionSiteClientDetails?.moaClientContact) {
		const contact =
			constructionSite.constructionSiteClientDetails.moaClientContact;
		return `${contact.firstname} ${contact.lastname}`;
	}

	return "Client non défini";
}

export const getRealEstateName = (constructionSite: GFAFullDetails) => {
	if (constructionSite.gfaShareholdersAndExperiences?.realEstateSociety) {
		return constructionSite.gfaShareholdersAndExperiences?.realEstateSociety
			.denomination;
	}
	return "Promoteur non défini";
};

export const getMOAName = (
	constructionSiteClientDetails: 
		(NonNullable<Parameters<typeof getClientNames>[0]> & {
			clientIsMOA: boolean | null;
			moaClientType: ClientType | null;
			moaClientSociety: { denomination: string } | null;
			moaClientContact: { firstname: string; lastname: string } | null;
		}) | null,
) => {
	if(!constructionSiteClientDetails) return null

	const { clientIsMOA, moaClientType, moaClientSociety, moaClientContact } =
		constructionSiteClientDetails

	if (clientIsMOA) {
		return getClientNames(constructionSiteClientDetails);
	}
	if (moaClientType === "SOCIETY" && moaClientSociety) {
		return moaClientSociety.denomination;
	}
	if (moaClientType === "USER" && moaClientContact) {
		return `${moaClientContact.firstname} ${moaClientContact.lastname}`;
	}
	return "- à venir";
};

export const getClientNames = (
	constructionSiteClientDetails: {
		clientType: ClientType | null
		clientSociety : {
			denomination: string
		} | null
		moaClientContact : {
			firstname: string
			lastname: string
		} | null
		constructionSiteClientContacts : {
			constructionSiteContact: {
				firstname: string
				lastname: string
			}
		}[]
	} | null
) => {
	const {
		clientType,
		clientSociety,
		moaClientContact,
		constructionSiteClientContacts,
	} = constructionSiteClientDetails || {};

	if (clientType === "SOCIETY" && clientSociety) {
		return [clientSociety.denomination];
	}
	if (clientType === "USER" && constructionSiteClientContacts) {
		return constructionSiteClientContacts.map(
			(contact) =>
				`${contact.constructionSiteContact.firstname} ${contact.constructionSiteContact.lastname}`,
		);
	}

	if (moaClientContact) {
		return [`${moaClientContact.firstname} ${moaClientContact.lastname}`];
	}

	return ["Client non défini"];
};

export const getSyndicName = (constructionSite: ConstructionSiteFullDetails | GFAFullDetails) => {
	return constructionSite.syndic?.denomination || null;
	
};

/**
 * Flattens a raw construction site document from Prisma to a simplified structure
 * @param rawDocument - Raw document from Prisma query with nested File relation
 * @returns Flattened document structure
 */
export const flattenConstructionSiteDocument = (
	rawDocument: ConstructionSiteDocumentRaw,
): ConstructionSiteDocument => {
	return {
		id: rawDocument.File.id,
		name: rawDocument.File.name,
		type: rawDocument.type,
		status: rawDocument.File.humanProcessingStatus,
		bucket: rawDocument.File.bucket,
		key: rawDocument.File.key,
		mimeType: rawDocument.File.mimeType,
		createdAt: rawDocument.File.createdAt.toISOString(),
		updatedAt: rawDocument.File.updatedAt.toISOString(),
		author: rawDocument.File.author,
	};
};

/**
 * Flattens multiple raw construction site documents
 * @param rawDocuments - Array of raw documents from Prisma
 * @returns Array of flattened documents
 */
export const flattenConstructionSiteDocuments = (
	rawDocuments: ConstructionSiteDocumentRaw[],
): ConstructionSiteDocument[] => {
	return rawDocuments.map(flattenConstructionSiteDocument);
};

export const getMOAFromConstructionSiteDetails = (
	constructionSiteDetails?: Prisma.ConstructionSiteClientDetailsGetPayload<{
		select: {
			clientIsMOA: true;
			// CLIENT
			clientType: true;
			constructionSiteClientContacts: {
				select: {
					constructionSiteContact: {
						select: {
							firstname: true;
							lastname: true;
							address: {
								select: {
									streetNumber: true;
									streetName: true;
									postalCode: true;
									city: true;
								};
							}
						};
					};
				};
				where: {
					constructionSiteContact: {
						role: "INSURED";
					};
				};
			};
			clientSociety: {
				select: {
					siren: true;
					denomination: true;
					headOfficeAddress: {
						select: {
							streetNumber: true;
							streetName: true;
							postalCode: true;
							city: true;
						};
					};
				};
			};
			// MOA
			moaClientType: true;
			moaClientSociety: {
				select: {
					siren: true;
					denomination: true;
					headOfficeAddress: {
						select: {
							streetNumber: true;
							streetName: true;
							postalCode: true;
							city: true;
						};
					};
				};
			};
			moaClientContact: {
				select: {
					firstname: true;
					lastname: true;
					address: {
						select: {
							streetNumber: true;
							streetName: true;
							postalCode: true;
							city: true;
						};
					};
				};
			};
		};
	}> | null,
) => {
	if (!constructionSiteDetails) return null;
	const {
		clientIsMOA,
		clientType,
		constructionSiteClientContacts,
		clientSociety,
		moaClientType,
		moaClientSociety,
		moaClientContact,
	} = constructionSiteDetails;
	if (clientIsMOA) {
		if (clientType === "SOCIETY") {
			return clientSociety;
		}
		if (clientType === "USER") {
			return constructionSiteClientContacts.map(
				(contact) => contact.constructionSiteContact,
			);
		}
	}
	if (moaClientType === "SOCIETY") {
		return moaClientSociety;
	}
	if (moaClientType === "USER") {
		return [moaClientContact];
	}
	return null;
};

export const getClientName = (
	constructionSite: ConstructionSiteList | GFAFullDetails,
) => {
	if (
		constructionSite.constructionSiteClientDetails?.clientType === "SOCIETY"
	) {
		return (
			constructionSite.constructionSiteClientDetails?.clientSociety
				?.denomination || "Société inconnue"
		);
	}

	const contacts =
		constructionSite.constructionSiteClientDetails
			?.constructionSiteClientContacts;

	if (contacts && contacts.length > 0) {
		return contacts
			.map((contact) => {
				return `${contact.constructionSiteContact.firstname} ${contact.constructionSiteContact.lastname}`;
			})
			.join(", ");
	}

	return "Client inconnu";
};

export const getBuildingTypeLabelName = (buildingType: string) => {
	const types: Record<string, string> = {
		APPARTMENT: "Appartement",
		INDIVIDUAL_HOUSE: "Maison individuelle",
		LIVING_BUILDING: "Immeuble à usage d'habitation",
		COMMERCIAL_BUILDING: "Bâtiment commercial",
		LOT_HOUSES: "Maisons individuelles en bande ou opération pavillonnaire",
		OFFICE_BUILDING: "Immeuble à usage de bureaux",
		MIXT_BUILDING: "Bâtiment à usage mixte",
		COMMON_PARTS: "Parties communes",
		PRIVATE_WORK: "Travaux privatifs",
	};
	return types[buildingType] || buildingType;
};

export const getActionTypesLabelNames = (types: string[]) => {
	const typeLabels: Record<string, string> = {
		CONSTRUCTION: "Construction",
		RENOVATION: "Rénovation",
		EXTENSION: "Extension",
		SYNDIC: "Syndic",
	};
	return types.map((type) => typeLabels[type] || type).join(", ");
};

export const getPrimeByProductType = (
	productType: string,
	propositions: Array<{
		productType: string;
		primeTTC: number;
		status: string;
	}> = [],
): number | null => {
	const proposition = propositions.find(
		(p) => p.productType === productType && p.status !== "SANS_SUITE",
	);
	return proposition?.primeTTC || null;
};
